import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft, Phone, Mail, Check, Flame, Snowflake, Thermometer, Car, Paperclip, FileText, Trash2,
  StickyNote, MessageCircle, CalendarPlus, CalendarClock, CheckCircle2, XCircle, Clock, User,
  ChevronDown, ChevronRight, AlertTriangle, Sparkles, ArrowRightLeft, Send, Headphones, Activity, Plus,
  ClipboardPaste, ImageIcon, Download, X as XIcon, Smile,
} from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { leadStages } from '../mock';
import { useAuth } from '../context/AuthContext';
import { ServiceStatusStrip } from './MySoldCars';

const REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🔥', '✅'];

const PIPE_STAGES = leadStages.filter((s) => s !== 'Perdido');
const input = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-[13px] outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const tempMeta = {
  'Quente': { label: 'HOT LEAD', cls: 'text-orange-400 bg-orange-400/12 border-orange-400/30', icon: <Flame className="w-3 h-3" /> },
  'Morno': { label: 'WARM', cls: 'text-amber-400 bg-amber-400/12 border-amber-400/30', icon: <Thermometer className="w-3 h-3" /> },
  'Frio': { label: 'COLD', cls: 'text-sky-400 bg-sky-400/12 border-sky-400/30', icon: <Snowflake className="w-3 h-3" /> },
};

const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();
const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;
const nvInput = 'w-full bg-muted/40 border border-border rounded px-2 py-1.5 text-[12px] outline-none focus:border-primary transition-colors';

const timeStr = (iso) => { try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const dayLabel = (iso) => {
  const d = new Date(iso); const t = new Date(); const y = new Date(Date.now() - 86400000);
  if (d.toDateString() === t.toDateString()) return 'Hoje';
  if (d.toDateString() === y.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};
const relTime = (iso) => {
  if (!iso) return '—';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(m) || m < 0) return '—';
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min atrás`;
  if (m < 1440) return `${Math.floor(m / 60)}h atrás`;
  return `${Math.floor(m / 1440)}d atrás`;
};

const classify = (a) => {
  const t = (a.action || '').toLowerCase();
  if (t.includes('ligação') || t.includes('call')) return { icon: Phone, tag: 'LIGAÇÃO', cls: 'text-sky-400 bg-sky-400/12', cat: 'calls' };
  if (t.includes('whatsapp')) return { icon: MessageCircle, tag: 'WHATSAPP', cls: 'text-emerald-400 bg-emerald-400/12', cat: 'messages' };
  if (t.includes('sms') || t.includes('mensagem')) return { icon: Send, tag: 'MENSAGEM', cls: 'text-emerald-400 bg-emerald-400/12', cat: 'messages' };
  if (t.includes('e-mail') || t.includes('email')) return { icon: Mail, tag: 'E-MAIL', cls: 'text-violet-400 bg-violet-400/12', cat: 'messages' };
  if (t.includes('note')) return { icon: StickyNote, tag: 'NOTA', cls: 'text-amber-400 bg-amber-400/12', cat: 'notes' };
  if (t.includes('follow-up')) return { icon: CalendarClock, tag: 'FOLLOW-UP', cls: 'text-violet-400 bg-violet-400/12', cat: 'activities' };
  if (t.includes('agendamento') || t.includes('appointment') || t.includes('test drive')) return { icon: CalendarPlus, tag: 'AGENDAMENTO', cls: 'text-teal-400 bg-teal-400/12', cat: 'activities' };
  if (t.includes('etapa') || t.includes('stage') || t.includes('movido') || t.includes('→')) return { icon: ArrowRightLeft, tag: 'ETAPA', cls: 'text-primary bg-primary/12', cat: 'stages' };
  if (t.includes('arquivo') || t.includes('file')) return { icon: Paperclip, tag: 'ARQUIVO', cls: 'text-muted-foreground bg-muted/40', cat: 'activities' };
  if (t.includes('criado') || t.includes('created')) return { icon: Sparkles, tag: 'LEAD', cls: 'text-primary bg-primary/12', cat: 'activities' };
  if (t.includes('atribuído') || t.includes('assigned') || t.includes('bdc')) return { icon: Headphones, tag: 'BDC', cls: 'text-sky-400 bg-sky-400/12', cat: 'activities' };
  return { icon: Activity, tag: 'ATIVIDADE', cls: 'text-muted-foreground bg-muted/40', cat: 'activities' };
};

const Section = ({ title, icon: Icon, children, action, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border/60 last:border-0">
      <div className="w-full flex items-center gap-2 px-4 py-3 text-left group">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 flex-1 text-left">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/60" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />}
          {Icon && <Icon className="w-3.5 h-3.5 text-primary" />}
          <span className="text-[11px] font-black uppercase tracking-widest text-foreground/80 flex-1">{title}</span>
        </button>
        {action}
      </div>
      {open && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-[11px] text-muted-foreground shrink-0">{label}</span>
    <span className={`text-[12px] font-medium text-right ${mono ? 'tabular-nums' : ''}`}>{value || '—'}</span>
  </div>
);

export const LeadWorkspace = ({ leadId, open, onClose, onChanged, onMoveStage }) => {
  const [data, setData] = useState(null);
  const { user: me } = useAuth();
  const [tab, setTab] = useState('all');
  const [note, setNote] = useState('');
  const [quick, setQuick] = useState(null);
  const [qForm, setQForm] = useState({});
  const [qSaving, setQSaving] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [pickVehicle, setPickVehicle] = useState(false);
  const [editVal, setEditVal] = useState(false);
  const [valInput, setValInput] = useState('');
  const [sendingMeta, setSendingMeta] = useState(false);
  const [newVeh, setNewVeh] = useState(null);
  const [savingVeh, setSavingVeh] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [notePics, setNotePics] = useState([]);
  const [noteUploading, setNoteUploading] = useState(false);
  const [picker, setPicker] = useState(null);
  const fileRef = useRef(null);
  const noteRef = useRef(null);
  const noteFileRef = useRef(null);
  const uploadRef = useRef(null);
  const noteUploadRef = useRef(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    if (!open || !leadId) return;
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imgs = items.filter((it) => it.type?.startsWith('image/')).map((it) => it.getAsFile()).filter(Boolean);
      if (!imgs.length) return;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const named = imgs.map((f, i) => new File([f], f.name && f.name !== 'image.png' ? f.name : `print-${stamp}${i ? `-${i + 1}` : ''}.png`, { type: f.type }));
      const handler = tabRef.current === 'files' ? uploadRef.current : noteUploadRef.current;
      if (!handler) return;
      e.preventDefault();
      handler(named);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, leadId]);

  const load = async () => { try { const r = await api.get(`/leads/${leadId}/full`); setData(r.data); } catch (e) {} };

  const saveValue = async (reset = false) => {
    setEditVal(false);
    try {
      await api.put(`/leads/${leadId}/value`, reset ? { reset: true } : { value: Number(valInput || 0) });
      load(); refresh && refresh();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const sendToMeta = async () => {
    setSendingMeta(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/send-to-meta`);
      toast({ title: 'Valor enviado para a Meta', description: `$${Number(data.value).toLocaleString('en-US')} · n8n ${data.http_status}` });
      load(); refresh && refresh();
    } catch (e) { toast({ title: 'Não foi possível enviar', description: e.response?.data?.detail || '' }); }
    finally { setSendingMeta(false); }
  };
  useEffect(() => {
    if (open && leadId) { setData(null); setTab('all'); setNote(''); setQuick(null); setPickVehicle(false); load(); }
    // eslint-disable-next-line
  }, [open, leadId]);

  if (!open) return null;
  const l = data?.lead;
  const b = data?.bdc_lead;
  const v = data?.vehicle;

  const refresh = () => { load(); onChanged && onChanged(); };

  const uploadNotePics = async (files) => {
    const imgs = (files || []).filter((f) => f.type?.startsWith('image/'));
    if (!imgs.length) return;
    setNoteUploading(true);
    try {
      const recs = [];
      for (const f of imgs) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post(`/leads/${leadId}/note-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        recs.push(data);
      }
      setNotePics((p) => [...p, ...recs]);
      noteRef.current?.focus();
    } catch (e) { toast({ title: 'Erro ao anexar imagem', description: e.response?.data?.detail || '' }); }
    finally { setNoteUploading(false); }
  };
  noteUploadRef.current = uploadNotePics;

  const react = async (aid, emoji) => {
    try { await api.post(`/leads/${leadId}/activities/${aid}/react`, { emoji }); refresh(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const addNote = async () => {
    if (!note.trim() && !notePics.length) return;
    try {
      await api.post(`/leads/${leadId}/note`, { note: note.trim(), attachments: notePics });
      setNote(''); setNotePics([]); refresh();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const saveQuick = async () => {
    setQSaving(true);
    try {
      if (quick === 'call') await api.post(`/leads/${leadId}/activity`, { type: 'call', outcome: qForm.outcome || 'Falou com o cliente', note: qForm.note || '' });
      else if (quick === 'message') await api.post(`/leads/${leadId}/activity`, { type: qForm.channel || 'whatsapp', note: qForm.note || '' });
      else if (quick === 'followup') {
        if (!qForm.title || !qForm.date) { toast({ title: 'Informe título e data' }); setQSaving(false); return; }
        await api.post(`/leads/${leadId}/followup`, { title: qForm.title, date: qForm.date, time: qForm.time || '' });
      } else if (quick === 'appointment') {
        if (!qForm.title || !qForm.date) { toast({ title: 'Informe título e data' }); setQSaving(false); return; }
        await api.post('/appointments', { titulo: qForm.title, cliente: l.nome, clienteId: l.customer_id || '', tipo: qForm.tipo || 'Visita', data: qForm.date, hora: qForm.time || '09:00', vendedor: l.vendedor || '' });
        await api.post(`/leads/${leadId}/activity`, { type: 'appointment', outcome: `${qForm.title} · ${qForm.date} ${qForm.time || ''}` });
      }
      toast({ title: 'Registrado com sucesso' });
      setQuick(null); setQForm({}); refresh();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setQSaving(false); }
  };

  const completeFollowup = async () => {
    try { await api.post(`/leads/${leadId}/followup/complete`); toast({ title: 'Follow-up concluído' }); refresh(); }
    catch (e) { toast({ title: 'Erro' }); }
  };

  const openVehiclePicker = async () => {
    setPickVehicle(true);
    try { const r = await api.get('/vehicles'); setVehicles(r.data.filter((x) => x.status !== 'Vendido' && x.status !== 'Entregue')); } catch (e) {}
  };
  const assignVehicle = async (vid) => {
    try { await api.put(`/leads/${leadId}/vehicle`, { vehicle_id: vid }); setPickVehicle(false); toast({ title: 'Veículo atribuído' }); refresh(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const createVehicle = async () => {
    if (!newVeh.marca || !newVeh.modelo) { toast({ title: 'Informe marca e modelo' }); return; }
    setSavingVeh(true);
    try {
      const { data: veh } = await api.post('/vehicles/quick', {
        marca: newVeh.marca, modelo: newVeh.modelo, trim: newVeh.trim || '', ano: Number(newVeh.ano) || 0,
        km: Number(newVeh.km) || 0, cor: newVeh.cor || '-', vin: newVeh.vin || '', preco: Number(newVeh.preco) || 0,
      });
      toast({ title: 'Veículo criado', description: `Stock #${veh.placa} — custo será lançado pela gerência` });
      setNewVeh(null);
      await assignVehicle(veh.id);
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSavingVeh(false); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        await api.post(`/leads/${leadId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast({ title: 'Arquivo(s) anexado(s)' }); refresh();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setUploading(false); }
  };
  const delFile = async (fid) => { try { await api.delete(`/leads/${leadId}/files/${fid}`); refresh(); } catch (e) {} };

  uploadRef.current = upload;

  const isImg = (f) => (f.content_type || '').startsWith('image/') || /\.(png|jpe?g|webp|gif|heic)$/i.test(f.original_filename || '');

  const isLost = l && (l.etapa === 'Perdido' || l.etapa === 'Fechado');
  const isWon = l && l.etapa === 'Contract Signed';
  const curIdx = l ? PIPE_STAGES.indexOf(l.etapa) : -1;
  const temp = l ? (tempMeta[l.temperatura] || tempMeta['Morno']) : null;
  const stageMins = l?.stage_entered_at ? Math.max(0, Math.floor((Date.now() - new Date(l.stage_entered_at).getTime()) / 60000)) : null;
  const fmtM = (m) => m == null ? '—' : m < 60 ? `${m}min` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
  const idleMins = l?.last_interaction_at ? Math.floor((Date.now() - new Date(l.last_interaction_at).getTime()) / 60000) : null;
  const idleLimit = l?.etapa === 'Novo' ? 15 : 1440;
  const atRisk = !isLost && !isWon && idleMins != null && idleMins >= idleLimit;

  const na = l?.next_action_data;
  const naOverdue = na && !na.done && na.date && new Date(`${na.date}T${na.time || '23:59'}:00`) < new Date();

  const acts = [...(data?.activities || [])].reverse();
  const filtered = tab === 'all' ? acts : acts.filter((a) => classify(a).cat === tab);
  const grouped = filtered.reduce((acc, a) => {
    const k = dayLabel(a.timestamp);
    (acc[acc.length - 1]?.key === k ? acc[acc.length - 1].items : (acc.push({ key: k, items: [] }), acc[acc.length - 1].items)).push(a);
    return acc;
  }, []);

  const TABS = [
    { id: 'all', label: 'Todos' }, { id: 'notes', label: 'Notas' }, { id: 'calls', label: 'Ligações' },
    { id: 'messages', label: 'Mensagens' }, { id: 'stages', label: 'Etapas' },
    { id: 'files', label: `Anexos${(l?.files || []).length ? ` (${l.files.length})` : ''}` },
  ];

  const QuickBtn = ({ id, icon: Icon, label, testid }) => (
    <button data-testid={testid} onClick={() => { setQuick(quick === id ? null : id); setQForm({}); }}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-[13px] font-semibold transition-colors ${quick === id ? 'border-primary bg-primary/12 text-primary' : 'border-border/70 text-foreground/80 hover:border-primary/40 hover:text-foreground'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-2 sm:p-5" data-testid="lead-workspace">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-[1220px] h-[94vh] rounded-2xl border border-border bg-card overflow-hidden flex flex-col animate-fade-up">
        {!l ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Carregando...</div>
        ) : (
          <>
            {/* HEADER */}
            <div className="shrink-0 border-b border-border/70 px-5 py-4 flex flex-wrap items-center gap-3">
              <button data-testid="lw-back" onClick={onClose} className="flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground border border-border rounded-lg px-2.5 py-2 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Voltar</button>
              <span className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-black shrink-0">{initials(l.nome)}</span>
              <div className="min-w-0">
                <h2 className="font-display font-black text-xl leading-tight truncate" data-testid="lw-name">{l.nome}</h2>
                <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
                  {l.telefone && <a href={`tel:${l.telefone}`} className="flex items-center gap-1 hover:text-foreground"><Phone className="w-3 h-3" />{l.telefone}</a>}
                  {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-foreground truncate"><Mail className="w-3 h-3" />{l.email}</a>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap ml-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${temp.cls}`} data-testid="lw-temp">{temp.icon}{temp.label}</span>
                <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${isLost ? 'text-red-400 bg-red-400/12 border-red-400/30' : isWon ? 'text-emerald-400 bg-emerald-400/12 border-emerald-400/30' : 'text-primary bg-primary/12 border-primary/30'}`} data-testid="lw-stage">{(l.etapa === 'Fechado' ? 'Perdido' : l.etapa).toUpperCase()}</span>
                {v && <span className="text-[11px] font-bold text-foreground/85 bg-muted/50 border border-border rounded-full px-2.5 py-1 tabular-nums">{money(v.preco)}</span>}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {!isWon && !isLost && (
                  <>
                    <button data-testid="lw-mark-won" onClick={() => { onClose(); onMoveStage(l.id, 'Contract Signed'); }} className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-400 border border-emerald-400/40 hover:bg-emerald-400/10 rounded-lg px-3 py-2 transition-colors"><CheckCircle2 className="w-4 h-4" /> Contrato</button>
                    <button data-testid="lw-mark-lost" onClick={() => { onClose(); onMoveStage(l.id, 'Perdido'); }} className="flex items-center gap-1.5 text-[12px] font-bold text-red-400 border border-red-400/40 hover:bg-red-400/10 rounded-lg px-3 py-2 transition-colors"><XCircle className="w-4 h-4" /> Perdido</button>
                  </>
                )}
              </div>
            </div>

            {isWon && l.deal_id && <div className="shrink-0 px-5 pt-3"><ServiceStatusStrip dealId={l.deal_id} /></div>}

            {/* PIPELINE */}
            <div className="shrink-0 border-b border-border/70 px-5 py-3 overflow-x-auto">
              {isLost ? (
                <div className="flex items-center gap-2 text-red-400 text-[13px] font-bold"><XCircle className="w-4 h-4" /> LEAD PERDIDO {l.lost_reason && <span className="text-red-400/70 font-medium">— {l.lost_reason}</span>}</div>
              ) : (
                <div className="flex items-center min-w-[560px] md:min-w-[720px]">
                  {PIPE_STAGES.map((s, i) => {
                    const done = i < curIdx || isWon;
                    const cur = i === curIdx && !isWon;
                    return (
                      <React.Fragment key={s}>
                        {i > 0 && <span className={`flex-1 h-[2px] mx-1 ${i <= curIdx || isWon ? 'bg-primary/60' : 'bg-border'}`} />}
                        <button data-testid={`lw-pipe-${s}`} onClick={() => !cur && (onClose(), onMoveStage(l.id, s))}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${cur ? 'border-primary bg-primary text-white shadow-glow' : done ? 'border-emerald-400/35 bg-emerald-400/10 text-emerald-400' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}>
                          {done ? <Check className="w-3 h-3" /> : cur ? <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> : null}
                          {s}
                          {cur && stageMins != null && <span className="text-[9px] font-medium opacity-80">· {fmtM(stageMins)}</span>}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            {/* WORKSPACE */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 overflow-y-auto lg:overflow-hidden">
              {/* LEFT */}
              <div className="lg:col-span-3 lg:overflow-y-auto border-b lg:border-b-0 lg:border-r border-border/70 bg-muted/[0.12]">
                <Section title="Resumo do Negócio" icon={FileText}>
                  <div className="flex items-start justify-between gap-2 py-1">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground/70">Valor da venda</span>
                    <div className="text-right">
                      {editVal ? (
                        <div className="flex items-center gap-1">
                          <input data-testid="lw-value-input" autoFocus className="cm-edit" value={valInput}
                            onChange={(e) => setValInput(e.target.value.replace(/[^0-9.]/g, ''))}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveValue(); if (e.key === 'Escape') setEditVal(false); }} />
                          <button data-testid="lw-value-save" onClick={() => saveValue()} className="ent-btn ent-btn-sm">OK</button>
                        </div>
                      ) : (
                        <button data-testid="lw-value" onClick={() => { setValInput(String(l.lead_value || 0)); setEditVal(true); }} className="num text-[15px] font-bold hover:text-primary transition-colors">
                          {money(l.lead_value || 0)}
                        </button>
                      )}
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 mt-0.5">
                        {l.value_source === 'manual' ? 'valor negociado' : l.value_origin === 'deal' ? 'valor do deal' : 'preço do estoque'}
                        {l.value_source === 'manual' && <button data-testid="lw-value-reset" onClick={() => saveValue(true)} className="ml-1.5 text-primary hover:underline">usar estoque</button>}
                      </p>
                    </div>
                  </div>
                  <button data-testid="lw-send-meta" onClick={sendToMeta} disabled={sendingMeta || !(l.lead_value > 0)}
                    className="ent-btn w-full justify-center h-8 mt-1 disabled:opacity-40">
                    {sendingMeta ? 'Enviando…' : l.meta_value_sent_at ? 'Reenviar valor para a Meta' : 'Enviar valor para a Meta'}
                  </button>
                  {l.meta_value_sent_at && <p className="text-[10px] t-ok mt-1 text-right">enviado {new Date(l.meta_value_sent_at).toLocaleString('pt-BR').slice(0, 16)} · {money(l.meta_value_sent || 0)}</p>}
                  <Row label="Temperatura" value={l.temperatura} />
                  <Row label="Fonte" value={l.origem} />
                  <Row label="Criado em" value={l.criadoEm} />
                  {b?.appointment_date && <Row label="Appointment BDC" value={`${b.appointment_date} ${b.appointment_time || ''}`} />}
                </Section>
                <Section title="Cliente" icon={User}>
                  <Row label="Nome" value={l.nome} />
                  <Row label="Telefone" value={l.telefone ? <a href={`tel:${l.telefone}`} className="text-primary hover:underline">{l.telefone}</a> : '—'} />
                  <Row label="E-mail" value={l.email ? <a href={`mailto:${l.email}`} className="text-primary hover:underline break-all">{l.email}</a> : '—'} />
                  <Row label="Vendedor" value={l.vendedor} />
                  {(b?.bdc_agent_name || l.bdc_agent) && <Row label="BDC Agent" value={b?.bdc_agent_name || l.bdc_agent} />}
                </Section>
                <Section title="Veículo de Interesse" icon={Car} action={<button onClick={(e) => { e.stopPropagation(); openVehiclePicker(); }} className="text-[10px] font-bold text-primary hover:underline" data-testid="lw-vehicle-btn">{l.vehicle_id ? 'Trocar' : 'Atribuir'}</button>}>
                  {v ? (
                    <>
                      {v.photos?.length > 0 && <img src={`${API}/files/${v.photos[0].path}`} alt="" className="w-full h-28 object-cover rounded-lg border border-border/70 mb-1" />}
                      <p className="text-[13px] font-bold">{v.ano} {v.marca} {v.modelo}</p>
                      <Row label="Stock #" value={v.placa} mono />
                      <Row label="Preço" value={money(v.preco)} mono />
                    </>
                  ) : (
                    <p className="text-[12px] text-muted-foreground">{l.interesse || 'Nenhum veículo atribuído.'}</p>
                  )}
                  {pickVehicle && (
                    <div className="rounded-lg border border-primary/30 bg-primary/[0.05] p-2 mt-2" data-testid="lw-vehicle-picker">
                      <button data-testid="lw-new-vehicle-btn" onClick={() => setNewVeh({ marca: '', modelo: '', ano: '', trim: '', vin: '', km: '', cor: '', preco: '' })}
                        className="w-full flex items-center gap-1.5 text-[12px] font-semibold text-primary border border-primary/40 rounded px-2 py-1.5 mb-1.5 hover:bg-primary/10 transition-colors">
                        <Plus className="w-3 h-3" /> Adicionar novo veículo
                      </button>
                      <div className="max-h-[180px] overflow-y-auto space-y-0.5">
                        {vehicles.map((x) => (
                          <button key={x.id} data-testid={`lw-pick-vehicle-${x.id}`} onClick={() => assignVehicle(x.id)} className="w-full text-left text-[12px] px-2 py-1.5 rounded hover:bg-muted/50 transition-colors">
                            {x.ano} {x.marca} {x.modelo} · #{x.placa} · <span className="num font-semibold">{money(x.preco)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {newVeh && (
                    <div className="rounded-lg border border-border bg-popover p-2.5 mt-2 space-y-2" data-testid="lw-new-vehicle-form">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Novo veículo no estoque</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <input data-testid="nv-marca" className={nvInput} placeholder="Marca *" value={newVeh.marca} onChange={(e) => setNewVeh({ ...newVeh, marca: e.target.value })} />
                        <input data-testid="nv-modelo" className={nvInput} placeholder="Modelo *" value={newVeh.modelo} onChange={(e) => setNewVeh({ ...newVeh, modelo: e.target.value })} />
                        <input data-testid="nv-ano" className={nvInput} placeholder="Ano" value={newVeh.ano} onChange={(e) => setNewVeh({ ...newVeh, ano: e.target.value })} />
                        <input data-testid="nv-km" className={nvInput} placeholder="Milhas" value={newVeh.km} onChange={(e) => setNewVeh({ ...newVeh, km: e.target.value })} />
                        <input data-testid="nv-vin" className={`${nvInput} col-span-2`} placeholder="VIN" value={newVeh.vin} onChange={(e) => setNewVeh({ ...newVeh, vin: e.target.value })} />
                        <input data-testid="nv-cor" className={nvInput} placeholder="Cor" value={newVeh.cor} onChange={(e) => setNewVeh({ ...newVeh, cor: e.target.value })} />
                        <input data-testid="nv-preco" className={nvInput} placeholder="Preço ($) *" value={newVeh.preco} onChange={(e) => setNewVeh({ ...newVeh, preco: e.target.value })} />
                      </div>
                      <p className="text-[10px] text-muted-foreground/70">O custo é lançado depois pela gerência no Estoque.</p>
                      <div className="flex gap-1.5">
                        <button onClick={() => setNewVeh(null)} className="flex-1 text-[11px] border border-border rounded py-1.5 text-foreground/65">Cancelar</button>
                        <button data-testid="nv-save" onClick={createVehicle} disabled={savingVeh} className="flex-1 text-[11px] font-bold bg-primary hover:bg-primary/90 rounded py-1.5 disabled:opacity-60">{savingVeh ? 'Salvando...' : 'Criar e atribuir'}</button>
                      </div>
                    </div>
                  )}
                </Section>
                {l.lost_reason && (
                  <Section title="Motivo da Perda" icon={XCircle}>
                    <p className="text-[12px] text-red-400/90">{l.lost_reason} <span className="text-muted-foreground/70">({l.lost_by})</span></p>
                  </Section>
                )}
              </div>

              {/* CENTER */}
              <div className="lg:col-span-6 flex flex-col min-h-[400px] lg:min-h-0">
                <div className="shrink-0 px-5 pt-4">
                  <h3 className="font-display font-black text-lg uppercase tracking-wide">Histórico</h3>
                  <div className="flex items-center gap-1 mt-2 border-b border-border/70 overflow-x-auto">
                    {TABS.map((t) => (
                      <button key={t.id} data-testid={`lw-tab-${t.id}`} onClick={() => setTab(t.id)}
                        className={`px-3 py-2 text-[12px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="lw-timeline">
                  {tab === 'files' ? (
                    <div className="space-y-3">
                      <div
                        data-testid="lw-dropzone"
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(Array.from(e.dataTransfer.files || [])); }}
                        className={`border border-dashed px-4 py-5 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/20'}`}>
                        <ClipboardPaste className="w-5 h-5 mx-auto text-primary/70" />
                        <p className="text-[12.5px] font-semibold mt-2">Cole um print com <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">Ctrl</kbd>+<kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">V</kbd> ou arraste arquivos aqui</p>
                        <p className="text-[11px] text-muted-foreground/70 mt-1">Imagens, PDF, Office, CSV, ZIP ou MP4 — até 20MB cada</p>
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <button data-testid="lw-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="text-[12px] px-3 py-2 rounded-lg font-semibold bg-muted/50 border border-border hover:bg-muted/80 transition-colors">
                            {uploading ? 'Enviando...' : '+ Escolher arquivo'}
                          </button>
                        </div>
                      </div>
                      <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" multiple className="hidden" onChange={(e) => { upload(Array.from(e.target.files)); e.target.value = ''; }} />

                      {(l.files || []).some(isImg) && (
                        <div className="grid grid-cols-3 gap-2" data-testid="lw-file-images">
                          {(l.files || []).filter(isImg).map((f) => (
                            <div key={f.id} className="relative group border border-border overflow-hidden bg-muted/30" data-testid={`lw-img-${f.id}`}>
                              <img src={`${API}/files/${f.path}`} alt={f.original_filename} onClick={() => setLightbox(f)}
                                className="w-full h-24 object-cover cursor-zoom-in" />
                              <div className="absolute inset-x-0 bottom-0 bg-background/85 px-1.5 py-1 flex items-center gap-1">
                                <span className="text-[9.5px] truncate flex-1 text-muted-foreground">{f.uploaded_by}</span>
                                <button data-testid={`lw-img-del-${f.id}`} onClick={() => delFile(f.id)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(l.files || []).filter((f) => !isImg(f)).map((f) => (
                        <div key={f.id} className="flex items-center gap-2 text-[12px] bg-muted/40 border border-border/70 rounded-lg px-3 py-2" data-testid={`lw-file-${f.id}`}>
                          <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                          <a href={`${API}/files/${f.path}`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1">{f.original_filename || 'arquivo'}</a>
                          <span className="text-muted-foreground/60 text-[10px]">{f.uploaded_by}</span>
                          <button onClick={() => delFile(f.id)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      {(l.files || []).length === 0 && <p className="text-[12px] text-muted-foreground/60 py-2">Nenhum anexo ainda — cole um print ou escolha um arquivo.</p>}
                    </div>
                  ) : (
                    <>
                      {grouped.map((g) => (
                        <div key={g.key} className="mb-4">
                          <p className="text-[10px] font-black tracking-[0.2em] text-muted-foreground/70 uppercase mb-2 sticky top-0 bg-card py-1">{g.key}</p>
                          <div className="space-y-1">
                            {g.items.map((a) => {
                              const c = classify(a); const Icon = c.icon;
                              return (
                                <div key={a.id} className="flex gap-3 rounded-lg px-2 py-2 hover:bg-muted/25 transition-colors">
                                  <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${c.cls}`}><Icon className="w-3.5 h-3.5" /></span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded ${c.cls}`}>{c.tag}</span>
                                      <span className="text-[11px] text-muted-foreground">{timeStr(a.timestamp)} · {a.user_name}</span>
                                    </div>
                                    <p className="text-[13px] font-medium mt-0.5">{a.action}</p>
                                    {a.note && <p className="text-[12px] text-foreground/65 mt-1 bg-muted/40 border border-border/60 rounded-md px-2.5 py-1.5">{a.note}</p>}
                                    {!!(a.attachments || []).length && (
                                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`lw-act-atts-${a.id}`}>
                                        {a.attachments.map((f) => (
                                          <button key={f.id} onClick={() => setLightbox(f)} data-testid={`lw-act-att-${f.id}`}
                                            className="w-20 h-14 border border-border overflow-hidden bg-muted/30 hover:border-primary/60 transition-colors">
                                            <img src={`${API}/files/${f.path}`} alt={f.original_filename} className="w-full h-full object-cover" />
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                      {Object.entries(a.reactions || {}).map(([emoji, users]) => {
                                        const mine = (users || []).some((u) => u.user_id === me?.user_id);
                                        return (
                                          <button key={emoji} data-testid={`lw-react-${a.id}-${emoji}`} onClick={() => react(a.id, emoji)}
                                            title={(users || []).map((u) => u.name).join(', ')}
                                            className={`text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${mine ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}>
                                            {emoji} {(users || []).length}
                                          </button>
                                        );
                                      })}
                                      <div className="relative">
                                        <button data-testid={`lw-react-btn-${a.id}`} onClick={() => setPicker(picker === a.id ? null : a.id)}
                                          className="text-[11px] px-1.5 py-0.5 rounded-full border border-dashed border-border text-muted-foreground/70 hover:text-primary hover:border-primary/50 transition-colors">
                                          <Smile className="w-3 h-3 inline" />
                                        </button>
                                        {picker === a.id && (
                                          <div className="absolute bottom-full left-0 mb-1 z-30 flex gap-0.5 bg-popover border border-border rounded-lg px-1.5 py-1 shadow-xl" data-testid={`lw-react-picker-${a.id}`}>
                                            {REACTIONS.map((emoji) => (
                                              <button key={emoji} data-testid={`lw-react-pick-${a.id}-${emoji}`} onClick={() => { react(a.id, emoji); setPicker(null); }}
                                                className="text-[15px] leading-none px-1 py-0.5 hover:scale-125 transition-transform">{emoji}</button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {filtered.length === 0 && <p className="text-sm text-muted-foreground/70 py-6 text-center">Sem registros nesta aba.</p>}
                    </>
                  )}
                </div>
                <div className="shrink-0 border-t border-border/70 px-5 py-3 space-y-2">
                  {!!notePics.length && (
                    <div className="flex flex-wrap gap-1.5" data-testid="lw-note-pending">
                      {notePics.map((f) => (
                        <div key={f.id} className="relative w-16 h-12 border border-border overflow-hidden">
                          <img src={`${API}/files/${f.path}`} alt="" className="w-full h-full object-cover" />
                          <button data-testid={`lw-note-pending-del-${f.id}`} onClick={() => setNotePics((p) => p.filter((x) => x.id !== f.id))}
                            className="absolute top-0 right-0 bg-background/85 p-0.5 text-red-400"><XIcon className="w-2.5 h-2.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input ref={noteRef} data-testid="lw-note-input" className={input} placeholder="Adicionar nota, comentário ou colar um print (Ctrl+V)..." value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()} />
                    <input ref={noteFileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadNotePics(Array.from(e.target.files)); e.target.value = ''; }} />
                    <button data-testid="lw-note-attach" onClick={() => noteFileRef.current?.click()} disabled={noteUploading} title="Anexar imagem ou print"
                      className="px-3 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors disabled:opacity-40">
                      {noteUploading ? <span className="text-[11px]">...</span> : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    <button data-testid="lw-note-save" onClick={addNote} disabled={!note.trim() && !notePics.length} className="px-4 rounded-lg text-[13px] font-semibold bg-primary hover:bg-primary/90 disabled:opacity-40 whitespace-nowrap transition-colors">Adicionar</button>
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="lg:col-span-3 lg:overflow-y-auto border-t lg:border-t-0 lg:border-l border-border/70 bg-muted/[0.12] p-4 space-y-4">
                <div className={`rounded-xl border p-4 ${naOverdue ? 'border-red-400/50 bg-red-400/[0.06]' : na ? 'border-primary/40 bg-primary/[0.05]' : 'border-border/70'}`} data-testid="lw-next-action">
                  <p className={`text-[10px] font-black tracking-[0.2em] uppercase mb-2 ${naOverdue ? 'text-red-400' : 'text-primary'}`}>{naOverdue ? '⚠ Atrasado' : 'Próxima Ação'}</p>
                  {na && !na.done ? (
                    <>
                      <p className="text-[14px] font-bold">{na.title}</p>
                      <p className="text-[12px] text-muted-foreground mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {na.date} {na.time}</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">por {na.created_by}</p>
                      <button data-testid="lw-complete-followup" onClick={completeFollowup} className="mt-3 w-full flex items-center justify-center gap-1.5 text-[12px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-400/40 hover:bg-emerald-500/25 rounded-lg py-2 transition-colors"><Check className="w-3.5 h-3.5" /> Concluir</button>
                    </>
                  ) : (
                    <>
                      <p className="text-[12px] text-muted-foreground">Nenhum follow-up agendado.</p>
                      <button data-testid="lw-schedule-followup" onClick={() => { setQuick('followup'); setQForm({}); }} className="mt-2.5 w-full text-[12px] font-bold text-primary border border-primary/40 hover:bg-primary/10 rounded-lg py-2 transition-colors">+ Agendar Follow-up</button>
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] font-black tracking-[0.2em] text-muted-foreground uppercase px-1">Ações Rápidas</p>
                  <QuickBtn id="call" icon={Phone} label="Registrar Ligação" testid="lw-qa-call" />
                  <QuickBtn id="message" icon={MessageCircle} label="Registrar Mensagem" testid="lw-qa-message" />
                  <QuickBtn id="followup" icon={CalendarClock} label="Agendar Follow-up" testid="lw-qa-followup" />
                  <QuickBtn id="appointment" icon={CalendarPlus} label="Criar Agendamento" testid="lw-qa-appointment" />
                </div>

                {quick && (
                  <div className="rounded-xl border border-primary/35 bg-primary/[0.04] p-3.5 space-y-2.5" data-testid="lw-quick-form">
                    {quick === 'call' && (
                      <>
                        <p className="text-[11px] font-black uppercase tracking-widest text-primary">Registrar Ligação</p>
                        <select className={input} value={qForm.outcome || 'Falou com o cliente'} onChange={(e) => setQForm({ ...qForm, outcome: e.target.value })} data-testid="lw-call-outcome">
                          {['Falou com o cliente', 'Não atendeu', 'Deixou recado', 'Número errado', 'Cliente retornará'].map((o) => <option key={o} className="bg-popover">{o}</option>)}
                        </select>
                        <textarea className={`${input} resize-none`} rows={2} placeholder="Notas da ligação..." value={qForm.note || ''} onChange={(e) => setQForm({ ...qForm, note: e.target.value })} data-testid="lw-call-note" />
                      </>
                    )}
                    {quick === 'message' && (
                      <>
                        <p className="text-[11px] font-black uppercase tracking-widest text-primary">Registrar Mensagem</p>
                        <select className={input} value={qForm.channel || 'whatsapp'} onChange={(e) => setQForm({ ...qForm, channel: e.target.value })}>
                          <option value="whatsapp" className="bg-popover">WhatsApp</option>
                          <option value="sms" className="bg-popover">SMS</option>
                          <option value="email" className="bg-popover">E-mail</option>
                        </select>
                        <textarea className={`${input} resize-none`} rows={2} placeholder="Resumo da mensagem..." value={qForm.note || ''} onChange={(e) => setQForm({ ...qForm, note: e.target.value })} />
                      </>
                    )}
                    {quick === 'followup' && (
                      <>
                        <p className="text-[11px] font-black uppercase tracking-widest text-primary">Agendar Follow-up</p>
                        <input className={input} placeholder="Ex: Ligar sobre financiamento" value={qForm.title || ''} onChange={(e) => setQForm({ ...qForm, title: e.target.value })} data-testid="lw-fu-title" />
                        <div className="grid grid-cols-2 gap-2">
                          <input type="date" className={input} value={qForm.date || ''} onChange={(e) => setQForm({ ...qForm, date: e.target.value })} data-testid="lw-fu-date" />
                          <input type="time" className={input} value={qForm.time || ''} onChange={(e) => setQForm({ ...qForm, time: e.target.value })} data-testid="lw-fu-time" />
                        </div>
                      </>
                    )}
                    {quick === 'appointment' && (
                      <>
                        <p className="text-[11px] font-black uppercase tracking-widest text-primary">Criar Agendamento</p>
                        <input className={input} placeholder="Ex: Test-drive Accord" value={qForm.title || ''} onChange={(e) => setQForm({ ...qForm, title: e.target.value })} />
                        <select className={input} value={qForm.tipo || 'Visita'} onChange={(e) => setQForm({ ...qForm, tipo: e.target.value })}>
                          {['Visita', 'Test-drive', 'Ligação', 'Retorno'].map((t) => <option key={t} className="bg-popover">{t}</option>)}
                        </select>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="date" className={input} value={qForm.date || ''} onChange={(e) => setQForm({ ...qForm, date: e.target.value })} />
                          <input type="time" className={input} value={qForm.time || ''} onChange={(e) => setQForm({ ...qForm, time: e.target.value })} />
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setQuick(null)} className="flex-1 text-[12px] font-medium border border-border rounded-lg py-2 text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                      <button data-testid="lw-quick-save" onClick={saveQuick} disabled={qSaving} className="flex-1 text-[12px] font-bold bg-primary hover:bg-primary/90 rounded-lg py-2 disabled:opacity-60 transition-colors">{qSaving ? 'Salvando...' : 'Salvar'}</button>
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-border/70 p-4 space-y-2" data-testid="lw-deal-health">
                  <p className="text-[10px] font-black tracking-[0.2em] text-muted-foreground uppercase">Saúde do Negócio</p>
                  <Row label="Temperatura" value={<span className={`inline-flex items-center gap-1 ${temp.cls.split(' ')[0]}`}>{temp.icon}{l.temperatura}</span>} />
                  <Row label="Último contato" value={relTime(l.last_interaction_at)} />
                  <Row label="Tempo na etapa" value={fmtM(stageMins)} />
                  {atRisk && (
                    <div className="flex items-start gap-2 text-[11px] font-semibold text-red-400 bg-red-400/[0.08] border border-red-400/25 rounded-lg px-2.5 py-2 mt-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Cliente sem contato há {fmtM(idleMins)}. Faça um follow-up!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[99] bg-background/95 flex items-center justify-center p-6" data-testid="lw-lightbox" onClick={() => setLightbox(null)}>
          <div className="max-h-full max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="w-3.5 h-3.5 text-primary" />
              <p className="text-[12px] font-semibold truncate flex-1">{lightbox.original_filename || 'print'}</p>
              <a href={`${API}/files/${lightbox.path}`} target="_blank" rel="noreferrer" className="ent-btn ent-btn-sm" data-testid="lw-lightbox-open"><Download className="w-3 h-3" /> Abrir</a>
              <button onClick={() => setLightbox(null)} className="ent-btn ent-btn-icon" data-testid="lw-lightbox-close"><XIcon className="w-3.5 h-3.5" /></button>
            </div>
            <img src={`${API}/files/${lightbox.path}`} alt={lightbox.original_filename} className="max-h-[80vh] w-auto mx-auto border border-border" />
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};
